<template lang='pug'>
  div
    div.d-flex.flex-row.align-baseline
      v-btn.ma-4(color='accent'
                :disabled="!hasChanges || isManagingCharacter"
                :loading="isSavingChanges"
                outlined
                @click="$emit('save-changes')") {{day6Resource.save}}
      v-btn.ma-4(color='accent'
                :disabled="isManagingCharacter"
                outlined
                @click="$emit('new-character')") {{dataBrowserResource.new}}
      p.body-1.mb-0(v-if="mainMessage") {{mainMessage}}
      div.flex-grow-1
      v-btn.ma-4(v-if="isSmallScreen"
                color='accent'
                outlined
                @click="$emit('close-browser')") {{commonResource.close}}
    virtual-list.list-container(
                :style="{ height: listHeight+'px' }"
                :data-key="'id'"
                :data-sources="itemData"
                :data-component="ListItem"
                v-on:tobottom="onScrollToBottom"
                @on-press-item='onItemPress'
                @on-edit-item='onEditCharacterData'
                @on-move-item-top='onItemMoveTop'
                @on-delete-item='onItemDelete')
      div(slot="footer")
        v-progress-circular.center-loading-indicator(
                            v-if="isListLoading"
                            :color='loadingIndicatorUi.color'
                            :width='loadingIndicatorUi.size'
                            indeterminate)
    v-dialog(v-model='showEditDialog'
            max-width="800"
            :fullscreen="isSmallScreen"
            :hide-overlay="isSmallScreen")
      CharacterDataEditor(ref='dataEditor'
                          :editingData="editingData"
                          :disable-name-input="disableInput.name"
                          :disable-sharing="disableInput.share"
                          :disable-notes-input="disableInput.notes"
                          @editor-action="onEditorAction"
                          @name-change="updateCharacterName"
                          @share-change="updateCharacterSharing"
                          @notes-change="updateCharacterNotes")
    FullDialog(ref='fullDialog' @dialog-action='onDialogAction')
</template>

<script>
import commonResource from '@/resources/common'
import day6Resource from '@/resources/day6'
import dataBrowserResource from '@/resources/dataBrowser'
import uiConstant from '@/resources/uiConstant'
import CharacterDataCache from '@/store/dataManager/CharacterDataCache'
import VirtualList from 'vue-virtual-scroll-list'
import ListItem from './character-data-list-item'
import CharacterDataEditor from './character-data-editor.vue'
import FullDialog from '../../dialogs/title-message-action-dialog'
import { getAccount } from '../../../util/accountUtil'
import ShareLevel from '@/constants/Day6ShareLevel'
import { characterDataRules } from '@/resources/inputRules'
import { createNamespacedHelpers } from 'vuex'
const { mapGetters, mapActions } = createNamespacedHelpers('day6CharacterBrowser')

const deleteCharacterDialogId = 1

export default {
  name: 'CharacterModelBrowser',
  components: { VirtualList, CharacterDataEditor, FullDialog },
  props: {
    characterIdSummary: String,
    hasChanges: Boolean,
    isSavingChanges: Boolean,
    isLoading: Boolean
  },
  data: function () {
    return {
      ListItem,
      commonResource,
      day6Resource,
      dataBrowserResource,
      loadingIndicatorUi: uiConstant.loadingIndicator,
      isSmallScreen: this.isSmallScreenVuetify(this.$vuetify),
      mainMessage: '',
      listHeight: window.innerHeight - 132,
      itemData: [],
      characterDataCache: new CharacterDataCache(),
      isListLoading: false,
      showEditDialog: false,
      characterInputRules: characterDataRules,
      editingData: {},
      isBlockingAction: false,
      disableInput: {
        name: false,
        share: false,
        notes: false
      }
    }
  },
  computed: {
    ...mapGetters([
      'isSavingName',
      'isSavingNotes',
      'isSharingCharacter'
    ]),
    accountId () {
      return getAccount().uid
    },
    isManagingCharacter () {
      return this.isSavingChanges ||
             this.isLoading ||
             this.isBlockingAction
    },
    editingItemId () {
      return (this.editingData || {}).id
    },
    itemsPerPage () {
      return Math.ceil(window.innerHeight / 48) + 2
    }
  },
  watch: {
    characterIdSummary (_current, previous) {
      if (!previous) {
        this.requeryNew()
      }
    }
  },
  mounted () {
    this.refresh()
  },
  methods: {
    ...mapActions([
      'saveCharacterName',
      'saveCharacterNotes',
      'saveCharacterSharing',
      'orderCharacterFirst',
      'deleteCharacter'
    ]),

    _makeItem (data) {
      return Object.assign(data, {
        getSelectedId: () => this.characterIdSummary,
        isActionDisabled: () => this.isManagingCharacter
        // TODO Show loading on item when character is being modified
      })
    },

    _updateList () {
      this.itemData.splice(0, this.itemData.length)
      const pageData = this.characterDataCache.pageData
      for (const data of pageData) {
        const item = this._makeItem(data)
        this.itemData.push(item)
      }

      this.mainMessage = this.itemData.length ? '' : dataBrowserResource.noCharacterData
    },

    closeBrowser () {
      this.showEditDialog = false
    },

    refresh () {
      const accountId = this.accountId
      this.characterDataCache.reset(accountId, this.itemsPerPage)
      this.loadMore()
    },

    async refreshCharacter (idSummary) {
      if (!idSummary) { return }

      await this.characterDataCache.refreshReorder(idSummary)
      this._updateList()
    },

    async loadMore () {
      try {
        this.isListLoading = true
        await this.characterDataCache.pageForward()
        this._updateList()
      } finally {
        this.isListLoading = false
      }
    },

    onScrollToBottom () {
      this.loadMore()
    },

    async requeryNew () {
      const newData = await this.characterDataCache.loadNewCharacter()
      if (newData) {
        const item = this._makeItem(newData)
        this.itemData.unshift(item)
      }
    },

    // TODO Make reactive not manual
    _updateInputsDisabled () {
      const editingId = this.editingItemId
      this.disableInput = {
        name: this.isSavingName(editingId),
        share: this.isSharingCharacter(editingId),
        notes: this.isSavingNotes(editingId)
      }
    },

    onItemPress ({ item }) {
      if (this.isManagingCharacter) { return }

      this.$emit('load-character', { idSummary: item.id, name: item.name })
    },

    onEditCharacterData ({ item }) {
      // Editing data is allowed as long as delete is not ongoing
      if (this.isBlockingAction) { return }

      this.editingData = item
      this.showEditDialog = true

      this._updateInputsDisabled()

      // TODO Find a more reliable way to update component that may not have mounted
      const setEditingItem = () => {
        const editor = this.$refs.dataEditor
        if (editor) {
          editor.setEditing(item)
          return true
        }
        return false
      }
      if (!setEditingItem()) {
        // Initially hidden
        setTimeout(() => setEditingItem(), 300)
      }
    },

    async onItemMoveTop ({ item, index }) {
      // Editing data is allowed as long as delete or setting modified is not ongoing
      if (this.isBlockingAction) { return }

      try {
        this.isBlockingAction = true

        const isMoved = await this.orderCharacterFirst(item.id)
        if (isMoved) {
          await this.characterDataCache.reorderToFirst({ id: item.id, index })
          this._updateList()
        } else {
          this.showOkDialog(dataBrowserResource.errorUpdatingTryAgain)
        }
      } finally {
        this.isBlockingAction = false
      }
    },

    onItemDelete ({ item }) {
      if (this.isManagingCharacter) { return }

      this._showConfirmDeleteDialog(item)
    },

    _showConfirmDeleteDialog (characterData) {
      this._deleteCharacterData = characterData
      this.$refs.fullDialog.showDialogProperties({
        message: dataBrowserResource.confirmDeleteCharacter(characterData),
        positiveAction: commonResource.yes,
        negativeAction: commonResource.no,
        id: deleteCharacterDialogId
      })
    },

    async completeCharacterDelete () {
      if (this.isManagingCharacter) { return }

      const data = this._deleteCharacterData
      if (!data) { return }

      this.isBlockingAction = true

      const idSummary = data.id

      const emitDelete = (isDeleting, isDeleted) => {
        this.$emit('on-character-delete', { idSummary, isDeleting, isDeleted })
      }

      try {
        emitDelete(true)

        const isDeleted = await this.deleteCharacter(idSummary)

        if (isDeleted) {
          await this.characterDataCache.deleteData(data)
          this._updateList()

          emitDelete(true, true)
        } else {
          this.showOkDialog(dataBrowserResource.errorDeletingTryAgain)
        }
      } finally {
        this._deleteCharacterData = null

        this.isBlockingAction = false

        emitDelete(false)
      }
    },

    onEditorAction () {
      this.editingData = null
      this.showEditDialog = false
    },

    async updateCharacterName ({ name }) {
      const characterData = this.editingData
      if (characterData.name === name ||
          (!characterData.name && !name)) { return }

      this.disableInput.name = true
      const isUpdated = await this.saveCharacterName({ id: characterData.id, name })
      this._updateInputsDisabled()
      if (isUpdated) {
        characterData.name = name
      } else {
        this.showOkDialog(dataBrowserResource.errorUpdatingTryAgain)
      }
    },
    async updateCharacterSharing ({ isSharing }) {
      const characterData = this.editingData
      const isSharingCharacter = characterData.shareLevel === ShareLevel.PUBLIC
      if (isSharingCharacter === isSharing) { return }

      this.disableInput.share = true
      const { success, shareId } = await this.saveCharacterSharing({
        idSummary: characterData.id,
        shareLevel: isSharing ? ShareLevel.PUBLIC : ShareLevel.PRIVATE
      })
      this._updateInputsDisabled()
      if (success) {
        characterData.shareId = shareId
        const shareLevel = shareId ? ShareLevel.PUBLIC : ShareLevel.PRIVATE
        characterData.shareLevel = shareLevel
        this.editingData.shareLevel = shareLevel
      } else {
        this.showOkDialog(dataBrowserResource.errorUpdatingTryAgain)
      }
    },
    async updateCharacterNotes ({ notes }) {
      const characterData = this.editingData
      if (characterData.notes === notes ||
          (!characterData.notes && !notes)) { return }

      this.disableInput.notes = true
      const isUpdated = await this.saveCharacterNotes({ id: characterData.id, notes })
      this._updateInputsDisabled()
      if (isUpdated) {
        characterData.notes = notes
      } else {
        this.showOkDialog(dataBrowserResource.errorUpdatingTryAgain)
      }
    },

    showOkDialog (message) {
      this.$refs.fullDialog.showMessageOkDialog(message)
    },

    onDialogAction (dialogData) {
      switch (dialogData.id) {
        case deleteCharacterDialogId:
          if (dialogData.action > 0) {
            this.completeCharacterDelete()
          }
          break
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.list-container {
  overflow-y: auto;
}
</style>
